import { Injectable, inject } from '@angular/core';
import { LoggerService } from '@optima-stride-ai/core';
import { environment } from '../../environments/environment';
import { Environment, validateEnvironment } from '../../environments/environment.interface';
import { ServingEnvironment } from '@optima-stride-ai/optima-app-framework';

/**
 * Environment Service
 * 
 * Provides centralized access to environment configuration
 * with validation and type safety.
 */
@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  private readonly env: Environment;
  private readonly logger = inject(LoggerService);

  constructor() {
    this.env = environment;
    
    // Validate environment configuration on initialization
    try {
      validateEnvironment(this.env);
    } catch (error) {
      this.logger.error(`Environment validation failed: ${error}`);
      if (this.env.production) {
        throw error; // Fail fast in production
      }
    }

    // Log environment info in development
    if (this.env.debugMode) {
    }
  }

  /**
   * Get the entire environment configuration
   */
  getEnvironment(): Readonly<Environment> {
    return this.env;
  }

  /**
   * Check if running in production mode
   */
  isProduction(): boolean {
    return this.env.production;
  }

  /**
   * Check if running in development mode
   */
  isDevelopment(): boolean {
    return this.env.name === ServingEnvironment.dev;
  }

  /**
   * Get environment name
   */
  getEnvironmentName(): ServingEnvironment {
    return this.env.name;
  }

  /**
   * Get main API URL
   */
  getApiUrl(): string {
    return this.env.apiUrl;
  }

  /**
   * Get Collabora URL
   */
  getCollaboraUrl(): string {
    return this.env.collaboraUrl;
  }

  /**
   * Get WOPI URL
   */
  getWopiUrl(): string {
    return this.env.wopiUrl;
  }

  /**
   * Get Whiteboard proxy URL
   */
  getWhiteboardProxyUrl(): string {
    return this.env.whiteboardProxyUrl;
  }

  /**
   * Get Platform API URL (HTTPS only)
   */
  getPlatformApiUrl(): string {
    return this.env.platformApiUrl;
  }

  /**
   * Check if Collabora is enabled
   */
  isCollaboraEnabled(): boolean {
    return this.env.collaboraEnabled;
  }

  /**
   * Check if debug mode is enabled
   */
  isDebugMode(): boolean {
    return this.env.debugMode;
  }

  /**
   * Build a full API URL from a relative path
   * @param path - Relative path (e.g., '/libraries' or 'libraries')
   * @returns Full URL
   */
  buildApiUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.env.apiUrl}${cleanPath}`;
  }

  /**
   * Build a full Collabora URL from a relative path
   * @param path - Relative path
   * @returns Full URL
   */
  buildCollaboraUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.env.collaboraUrl}${cleanPath}`;
  }

  /**
   * Build a full Platform API URL from a relative path
   * @param path - Relative path
   * @returns Full URL
   */
  buildPlatformApiUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.env.platformApiUrl}${cleanPath}`;
  }

  /**
   * Get all API endpoints as an object
   */
  getApiEndpoints(): Record<string, string> {
    return {
      main: this.env.apiUrl,
      collabora: this.env.collaboraUrl,
      wopi: this.env.wopiUrl,
      whiteboard: this.env.whiteboardProxyUrl,
      platform: this.env.platformApiUrl,
      benzaiten: this.env.hayagriva.appServer.baseURL
    };
  }

  /**
   * Get hayagriva configuration
   */
  gethayagrivaConfig() {
    return this.env.hayagriva;
  }

  /**
   * Get hayagriva App Server URL
   */
  gethayagrivaAppServerURL() {
    return {
      baseURL: this.env.hayagriva.appServer.baseURL,
      extension: this.env.hayagriva.appServer.extension,
      version: this.env.hayagriva.appServer.version
    };
  }

  /**
   * Get hayagriva App Headers
   */
  gethayagrivaAppHeaders() {
    return {
      tenantuuid: this.env.hayagriva.headers.tenantuuid,
      appid: this.env.hayagriva.headers.appid,
      userToken: this.env.hayagriva.headers.userToken
    };
  }

  /**
   * Build full hayagriva API URL
   * Combines baseURL, extension, and version
   * @example buildhayagrivaApiUrl() => 'https://optimastride.com/api/v0'
   */
  buildhayagrivaApiUrl(): string {
    const { baseURL, extension, version } = this.env.hayagriva.appServer;
    const parts = [baseURL];
    
    if (extension) parts.push(extension);
    if (version) parts.push(version);
    
    return parts.join('/');
  }

  /**
   * Build full hayagriva API URL with additional path
   * @param path - Additional path to append
   * @example buildhayagrivaApiUrlWithPath('/biz-entity-type') => 'https://optimastride.com/api/v0/biz-entity-type'
   */
  buildhayagrivaApiUrlWithPath(path: string): string {
    const baseUrl = this.buildhayagrivaApiUrl();
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  }

  /**
   * Update hayagriva headers (for runtime configuration)
   * @param headers - Partial headers to update
   */
  updatehayagrivaHeaders(headers: Partial<{tenantuuid: string; appid: string; userToken: string}>) {
    if (headers.tenantuuid !== undefined) {
      this.env.hayagriva.headers.tenantuuid = headers.tenantuuid;
    }
    if (headers.appid !== undefined) {
      this.env.hayagriva.headers.appid = headers.appid;
    }
    if (headers.userToken !== undefined) {
      this.env.hayagriva.headers.userToken = headers.userToken;
    }

    if (this.env.debugMode) {
      // Headers updated
    }
  }

  /**
   * Log environment info to console (development only)
   */
  logEnvironmentInfo(): void {
    // TODO: Environment logging removed for production
  }
}
